<template>
    <nav class="nav-chips" aria-label="Section">
        <ul ref="listRef">
            <li v-for="chip in chips" :key="chip.to">
                <NuxtLink
                    :to="chip.to"
                    :class="{ 'is-active': isActive(chip) }"
                    :aria-current="isActive(chip) ? 'page' : undefined"
                    :ref="(el) => setChipRef(el, chip)"
                >
                    <component :is="chip.icon" class="nav-chips-icon" />
                    <span>{{ chip.label }}</span>
                </NuxtLink>
            </li>
        </ul>
    </nav>
</template>

<script setup>
    import { SvgsHome, SvgsUser, SvgsSite, SvgsArea, SvgsSiteTourBn, SvgsEvent } from '#components';

    const route = useRoute();

    // Mirrors the bottom navigation's destinations, in the order the comp shows them.
    const chips = [
        { label: 'Home', to: '/', icon: SvgsHome, exact: true },
        { label: 'My OHC', to: '/my-ohc/', icon: SvgsUser },
        { label: 'Sites', to: '/sites/', icon: SvgsSite },
        { label: 'Neighborhood Areas', to: '/neighborhoods/', icon: SvgsArea },
        { label: 'Itineraries', to: '/itineraries/', icon: SvgsSiteTourBn },
        { label: 'Events', to: '/events/', icon: SvgsEvent },
    ];

    // Detail pages (/sites/<id>) should keep their section chip lit, so match on
    // prefix — except Home, which would otherwise match every route.
    const isActive = (chip) => {
        const path = route.path.endsWith('/') ? route.path : `${route.path}/`;
        if (chip.exact) return path === '/';
        return path.startsWith(chip.to);
    };

    const listRef = ref(null);
    const activeEl = ref(null);
    const setChipRef = (el, chip) => {
        if (el && isActive(chip)) activeEl.value = el.$el ?? el;
    };

    // The row scrolls horizontally, so the current section can start off-screen.
    // Bring it into view without scrolling the page itself.
    onMounted(() => {
        nextTick(() => {
            const el = activeEl.value;
            const list = listRef.value;
            if (!el || !list) return;
            const offset = el.offsetLeft - (list.clientWidth - el.clientWidth) / 2;
            list.scrollTo({ left: Math.max(0, offset), behavior: 'instant' });
        });
    });
</script>

<style lang="scss" scoped>
    // Mobile-only: a sticky section switcher directly under the fixed header.
    // Its height feeds --sticky-bar-height in layouts/default.vue, which is what
    // pushes #main clear of both bars — so changing the height here is enough.
    .nav-chips {
        position: fixed;
        top: var(--header-height);
        left: 0;
        right: 0;
        z-index: 98; // header is 99

        height: var(--chip-bar-height);
        background: $white;

        @media (min-width: $tablet) {
            display: none;
        }

        ul {
            display: flex;
            align-items: center;
            gap: 8px;
            height: 100%;
            padding: 0 16px;

            overflow-x: auto;
            overflow-y: hidden;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;

            &::-webkit-scrollbar {
                display: none;
            }
        }

        li {
            flex: 0 0 auto;
        }

        a {
            display: flex;
            align-items: center;
            gap: 6px;

            // 7px made a 28px chip — a small target on a phone, which is what
            // prompted the "fat fingers" request (Asana O2-18). 11px takes it to
            // 36px, and --chip-bar-height grows with it so the chips keep their
            // breathing room above and below rather than filling the bar.
            // Horizontal padding is untouched; only the vertical was asked for.
            padding: 11px 10px;
            background: $gray-light;

            font-family: $body;
            font-size: 12px;
            font-weight: $heavy;
            line-height: 1;
            letter-spacing: 0.5px;
            text-transform: uppercase;
            white-space: nowrap;
            color: $black;

            .nav-chips-icon :deep(.stroke) {
                stroke: $black;
            }

            .nav-chips-icon :deep(.fill) {
                fill: $black;
            }

            &.is-active {
                background: $red;
                color: $white;

                .nav-chips-icon :deep(.stroke) {
                    stroke: $white;
                }

                .nav-chips-icon :deep(.fill),
                .nav-chips-icon :deep(.fill-active-red) {
                    fill: $white;
                }

                // This variant exists to punch a hole in a filled glyph, so on
                // the red chip it has to become the chip colour, not white.
                .nav-chips-icon :deep(.fill-active-gray) {
                    fill: $red;
                }
            }

            &:focus-visible {
                outline: 2px solid $black;
                outline-offset: 2px;
            }
        }

        .nav-chips-icon {
            display: block;
            width: 14px;
            height: 14px;
            flex: 0 0 14px;

            // `align-items: center` on the chip centres this on the LINE BOX, but
            // uppercase text only inks its cap height sitting on the baseline, so
            // the icon reads low against it. Measured on the rendered chip: 8.18px
            // of cap height in an 11px line box leaves the icon 1.39px below the
            // text's optical centre (Asana O2-20).
            //
            // transform rather than margin, so the flex line height — and with it
            // the chip height that --chip-bar-height is sized against — is
            // untouched. Same approach as the site label's icon.
            transform: translateY(-1.4px);
        }
    }
</style>
